"use client";

import Link from "next/link";
import { FaArrowRight, FaClock } from "react-icons/fa";
import type { ArticleSummary } from "@/data/articles";
import { saveGuidesScrollPosition } from "@/app/guides/_components/guides-scroll";
import {
  GUIDE_ARTICLE_CARD_RADIUS,
  GUIDE_ARTICLE_LAYOUT_TRANSITION,
  guideArticleLayoutId,
} from "@/lib/guide-article-motion";
import { motion, useReducedMotion } from "framer-motion";

type GuideArticleCardProps = {
  article: ArticleSummary;
  compact?: boolean;
};

const CARD_SHADOW =
  "shadow-[0_20px_50px_-16px_rgba(0,0,0,0.45),0_6px_20px_-8px_rgba(0,0,0,0.25)] group-hover:shadow-[0_24px_56px_-18px_rgba(16,185,129,0.28)]";

export function GuideArticleCard({ article, compact = false }: GuideArticleCardProps) {
  const reducedMotion = useReducedMotion();
  const layoutId = reducedMotion ? undefined : guideArticleLayoutId(article.slug);

  return (
    <div
      className={`group h-full overflow-hidden ${GUIDE_ARTICLE_CARD_RADIUS} transition-shadow duration-300 ${CARD_SHADOW}`}
    >
      <Link
        href={`/guides/${article.slug}`}
        className="block h-full"
        onClick={saveGuidesScrollPosition}
      >
        <motion.div
          layout={!reducedMotion}
          layoutId={layoutId}
          transition={GUIDE_ARTICLE_LAYOUT_TRANSITION}
          className={`relative flex h-full min-h-0 flex-col overflow-hidden ${GUIDE_ARTICLE_CARD_RADIUS} border border-white/10 bg-[#1C1C1C] transition-colors duration-300 hover:border-emerald-500/30 ${
            compact ? "p-4" : "p-5 sm:p-6"
          }`}
        >
          <div
            aria-hidden
            className={`pointer-events-none absolute inset-0 overflow-hidden opacity-0 transition-opacity duration-300 group-hover:opacity-100 ${GUIDE_ARTICLE_CARD_RADIUS}`}
          >
            <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-emerald-500/10 to-transparent" />
          </div>

          <div className={`relative flex items-start justify-end ${compact ? "mb-2" : "mb-4"}`}>
            <span
              className={`inline-flex shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/5 text-zinc-400 transition-all duration-300 group-hover:border-emerald-500/40 group-hover:bg-emerald-500/10 group-hover:text-emerald-400 ${
                compact ? "h-7 w-7" : "h-8 w-8"
              }`}
            >
              <FaArrowRight className="text-[10px] -rotate-45" aria-hidden="true" />
            </span>
          </div>

          <h3
            className={`relative line-clamp-2 font-bold leading-snug tracking-tight text-white ${
              compact ? "mb-2 text-base sm:text-[1.05rem]" : "mb-3 text-lg sm:text-xl"
            }`}
          >
            {article.title}
          </h3>

          <p
            className={`relative line-clamp-2 flex-1 leading-relaxed text-zinc-400 ${
              compact ? "mb-3 text-xs sm:text-sm" : "mb-6 text-sm sm:text-[0.9375rem]"
            }`}
          >
            {article.summary}
          </p>

          <div
            className={`relative mt-auto flex items-center justify-between gap-3 border-t border-dashed border-white/10 text-xs text-zinc-500 ${
              compact ? "pt-3 sm:text-[0.75rem]" : "pt-4 sm:text-[0.8125rem]"
            }`}
          >
            <span className="inline-flex items-center gap-1.5 font-medium">
              <FaClock className="text-[10px] text-zinc-500" aria-hidden="true" />
              {article.readTime}
            </span>
            <time dateTime={article.publishedAt}>
              {new Date(article.publishedAt).toLocaleDateString("fr-FR", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </time>
          </div>
        </motion.div>
      </Link>
    </div>
  );
}
